import {
  ArrowRightLeft,
  Ban,
  CircleCheck,
  CircleSlash,
  Clock,
  FileText,
  Send,
  ThumbsUp,
  Truck,
} from 'lucide-react'
import { type ReactNode } from 'react'

import type { SalesDocumentSummary } from '@aeris/contracts'
import {
  documentStatusText,
  onlyInLabel as onlyInText,
  SALES_STATUS_TEXT,
  STATUSES_BY_KIND as STATUSES,
  type SalesStatusText,
} from '@aeris/status-meta'
import { type StatusTone } from '@aeris/ui'

/**
 * O que cada situação de um documento de venda QUER DIZER — F156.
 *
 * O rótulo, a cor e a frase moram em `@aeris/status-meta` desde a F305, para
 * o app do celular dizer as mesmas palavras. Aqui o site só acrescenta o
 * ícone — e continua exportando os mesmos nomes de antes, então nenhuma tela
 * mudou.
 *
 * ## Só constantes, nenhum componente
 *
 * Um módulo que exporta as duas coisas deixa de ser fronteira de Fast Refresh
 * (`react-refresh/only-export-components`). O selo que consome isto é
 * `components/status-badge.tsx`.
 */

type Kind = SalesDocumentSummary['kind']
type Status = SalesDocumentSummary['status']

export type StatusMeta = Omit<SalesStatusText, 'tone'> & {
  readonly tone: StatusTone
  readonly icon: ReactNode
}

const icon = (Glyph: typeof Send): ReactNode => (
  <Glyph aria-hidden="true" className="h-3 w-3" />
)

const ICONS: Record<Status, ReactNode> = {
  draft: icon(FileText),
  sent: icon(Send),
  accepted: icon(ThumbsUp),
  rejected: icon(CircleSlash),
  expired: icon(Clock),
  confirmed: icon(CircleCheck),
  fulfilled: icon(Truck),
  completed: icon(CircleCheck),
  converted: icon(ArrowRightLeft),
  cancelled: icon(Ban),
}

/** Em que tipo de documento cada situação existe, derivado da máquina de estados. */
export const STATUSES_BY_KIND: Readonly<Record<Kind, readonly Status[]>> =
  STATUSES

export const STATUS_META: Readonly<Record<Status, StatusMeta>> =
  Object.fromEntries(
    (Object.keys(SALES_STATUS_TEXT) as Status[]).map((status) => [
      status,
      { ...SALES_STATUS_TEXT[status], icon: ICONS[status] },
    ])
  ) as Record<Status, StatusMeta>

/** "só em pedido" — a parte da explicação que depende do tipo de documento. */
export const onlyInLabel = (
  meta: StatusMeta,
  kindLabels: Record<Kind, string>
): string | null => onlyInText(meta, kindLabels)

/**
 * A situação que a tela mostra para UM documento (Eduardo, 16/10): o pedido
 * entregue e acertado aparece "Concluído". A regra está no pacote.
 */
export const documentStatusMeta = (document: {
  readonly kind: Kind
  readonly status: Status
  readonly exchangeScope?: string | undefined
}): StatusMeta => {
  const { settled, ...text } = documentStatusText(document)
  return {
    ...text,
    icon: settled ? icon(CircleCheck) : ICONS[document.status],
  }
}
